import { useEffect, useMemo, useState } from 'react';
import { LabThreeIntro } from './LabExplorer';
import PaxosNetwork from './PaxosNetwork';
import { paxosScenarios } from './paxosScenarios';
import { guidedLink } from '../dslabs/ui/guidedLinks';

const nodeLabel = (id) => {
    if (!id) return 'System';
    if (id === 'client') return 'Client';
    return id.replace('server', 'Server ');
};

function eventPayload(frame) {
    const sender = frame.nodes.find(({ id }) => id === frame.from);
    const target = frame.nodes.find(({ id }) => frame.to?.includes(id));
    const sourceLog = sender?.log || target?.log || [];
    const activeSlotIndex = sourceLog.reduce((last, entry, index) => (entry.status !== 'EMPTY' ? index : last), -1);
    const activeSlot = sourceLog[activeSlotIndex];

    if (frame.event === 'ProposeLeaderRequest') return [['ballot', sender?.ballot || 'next ballot']];
    if (frame.event === 'ProposeLeaderReply') return [['accepted', 'true'], ['promisedBallot', sender?.promised || 'current'], ['log', 'local accepted + chosen slots']];
    if (frame.event === 'PaxosRequest') return [['command', activeSlot?.command || 'client operation'], ['sequenceNumber', '1']];
    if (frame.event.includes('AcceptCommandRequest')) return [['slotNumber', activeSlotIndex + 1 || 1], ['ballot', sender?.ballot || 'current'], ['command', activeSlot?.command || 'proposed value']];
    if (frame.event === 'AcceptCommandReply') return [['accepted', 'true'], ['slotNumber', activeSlotIndex + 1 || 1], ['leaderBallot', target?.ballot || 'current']];
    if (frame.event === 'PingRequest') return [['leaderBallot', sender?.ballot || 'current'], ['lastExecuted', sender?.executed ?? 0], ['logEntries', sourceLog.filter(({ status }) => status !== 'EMPTY').length]];
    if (frame.event === 'PingReply') return [['lastExecuted', sender?.executed ?? 0], ['purpose', 'global compaction watermark']];
    if (frame.event === 'GarbageCollectionRequest') return [['clearThrough', 'slot 3'], ['requires', 'all configured replicas executed']];
    if (frame.event === 'AcceptCommandTimer' || frame.event === 'PingCheckTimer') return [['timer', frame.event], ['action', 'retry / begin election']];
    if (frame.event === 'PaxosReply') return [['result', 'OK'], ['sequenceNumber', '1']];
    if (frame.type === 'FAULT') return [['network state', frame.event], ['affected nodes', frame.summary]];
    if (frame.type === 'UNSUPPORTED' || frame.type === 'DESIGN' || frame.type === 'LAB 4') return [['operation', frame.event], ['status', frame.type]];
    return [['state', frame.event], ['quorum', frame.quorum]];
}

function stateChanges(frame, previousFrame) {
    if (!previousFrame) return [`Created ${frame.nodes.length} PaxosServer replicas`, 'Initialized three empty log slots per replica'];

    const changes = [];
    frame.nodes.forEach((node) => {
        const previous = previousFrame.nodes.find(({ id }) => id === node.id);
        if (!previous) {
            changes.push(`${node.label} appeared as ${node.role}`);
            return;
        }
        if (previous.health !== node.health) changes.push(`${node.label}: ${previous.health} → ${node.health}`);
        if (previous.role !== node.role) changes.push(`${node.label}: role ${previous.role} → ${node.role}`);
        if (previous.promised !== node.promised) changes.push(`${node.label}: promised ${node.promised}`);
        if (previous.leader !== node.leader) changes.push(`${node.label}: leader = ${node.leader}`);
        if (previous.executed !== node.executed) changes.push(`${node.label}: executed through slot ${node.executed}`);
        node.log.forEach((entry, index) => {
            const oldEntry = previous.log[index];
            if (JSON.stringify(oldEntry) !== JSON.stringify(entry)) {
                changes.push(`${node.label} slot ${index + 1}: ${oldEntry?.status || 'NONE'} → ${entry.status}${entry.command !== '—' ? ` (${entry.command})` : ''}`);
            }
        });
    });
    return changes.length ? changes : ['No durable replica state changed in this step'];
}

function StepDetails({ frame, previousFrame }) {
    const targets = frame.to?.map(nodeLabel).join(', ') || '—';
    const changes = stateChanges(frame, previousFrame);
    return (
        <section className="step-details" aria-label="Current event details">
            <div className="step-detail-card message-envelope">
                <span className="detail-kicker">Message envelope</span>
                <h3>{frame.event}</h3>
                <div className="message-route"><code>{nodeLabel(frame.from)}</code><span>→</span><code>{targets}</code></div>
                <dl>{eventPayload(frame).map(([name, value]) => <div key={name}><dt>{name}</dt><dd>{value}</dd></div>)}</dl>
            </div>
            <div className="step-detail-card">
                <span className="detail-kicker">State changed</span>
                <ul>{changes.map((change) => <li key={change}>{change}</li>)}</ul>
            </div>
            <div className="step-detail-card">
                <span className="detail-kicker">Why it matters</span>
                <h3>{frame.invariant}</h3><p>{frame.detail}</p><code className="handler-name">{frame.handler}</code>
            </div>
        </section>
    );
}

function LogInspector({ node }) {
    return (
        <div className="paxos-inspector">
            <div className="inspector-heading">
                <div><span>Selected node</span><h2>{node.label}</h2></div>
                <span className={`role-pill is-${node.role}`}>{node.role}</span>
            </div>
            <dl className="node-state-grid">
                <div><dt>Self ballot</dt><dd>{node.ballot}</dd></div>
                <div><dt>Promised</dt><dd>{node.promised}</dd></div>
                <div><dt>Leader</dt><dd>{node.leader}</dd></div>
                <div><dt>Executed</dt><dd>slot {node.executed}</dd></div>
            </dl>
            <div className="log-heading"><span>Local replicated log</span><span>{node.log.filter(({ status }) => status !== 'EMPTY').length} non-empty</span></div>
            <div className="log-table" role="table" aria-label={`${node.label} local log`}>
                <div className="log-row log-table-head" role="row">
                    <span role="columnheader">Slot</span><span role="columnheader">Status</span><span role="columnheader">Command</span>
                </div>
                {node.log.map((entry, index) => (
                    <div className="log-row" role="row" key={`${node.id}-slot-${index + 1}`}>
                        <span className="slot-number" role="cell">{String(index + 1).padStart(2, '0')}</span>
                        <span role="cell"><span className={`slot-status is-${entry.status.toLowerCase()}`}>{entry.status}</span></span>
                        <span className="slot-command" role="cell">{entry.command}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function TraceRail({ frames, currentIndex, onSelect }) {
    return (
        <aside className="trace-rail" aria-label="Event trace">
            <div className="panel-label"><span>Trace</span><span>{currentIndex}/{frames.length - 1}</span></div>
            <div className="trace-list">
                {frames.map((frame, index) => (
                    <button
                        className={`trace-event${index === currentIndex ? ' is-current' : ''}${index < currentIndex ? ' is-past' : ''}`}
                        key={frame.id}
                        type="button"
                        onClick={() => onSelect(index)}
                        aria-current={index === currentIndex ? 'step' : undefined}
                    >
                        <span className="trace-index">{String(index).padStart(2, '0')}</span>
                        <span className="trace-copy"><strong>{frame.event}</strong><small>{frame.phase}</small></span>
                        <span className="trace-arrow">→</span>
                    </button>
                ))}
            </div>
        </aside>
    );
}

export default function PaxosExperience({ lab, onTryInSandbox }) {
    const [scenarioId, setScenarioId] = useState(paxosScenarios[0].id);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedNode, setSelectedNode] = useState('server1');
    const [playing, setPlaying] = useState(false);
    const scenario = useMemo(() => paxosScenarios.find(({ id }) => id === scenarioId) || paxosScenarios[0], [scenarioId]);
    const frame = scenario.frames[currentIndex];
    const previousFrame = currentIndex > 0 ? scenario.frames[currentIndex - 1] : null;
    const inspectedNode = frame.nodes.find(({ id }) => id === selectedNode) || frame.nodes[0];
    const atEnd = currentIndex === scenario.frames.length - 1;

    useEffect(() => {
        if (!playing) return undefined;
        if (atEnd) {
            setPlaying(false);
            return undefined;
        }
        const timer = window.setTimeout(() => setCurrentIndex((index) => index + 1), 1650);
        return () => window.clearTimeout(timer);
    }, [playing, atEnd, currentIndex]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.target.closest?.('button, a, [role="button"]')) return;
            if (event.key === 'ArrowRight') setCurrentIndex((index) => Math.min(index + 1, scenario.frames.length - 1));
            if (event.key === 'ArrowLeft') setCurrentIndex((index) => Math.max(index - 1, 0));
            if (event.key === ' ') {
                event.preventDefault();
                setPlaying((value) => !value);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [scenario.frames.length]);

    const chooseScenario = (id) => {
        setScenarioId(id); setCurrentIndex(0); setSelectedNode('server1'); setPlaying(false);
    };

    return (
        <>
            <LabThreeIntro lab={lab} />
            <section className="debugger-shell" aria-label="Interactive Paxos debugger">
                <div className="debugger-titlebar">
                    <div className="window-dots" aria-hidden="true"><i /><i /><i /></div>
                    <span>DSLabs Visual Debugger — PaxosServer</span><span className="debugger-mode">DETERMINISTIC TRACE</span>
                </div>
                <div className="paxos-orientation">
                    <div><span className="orientation-number">01</span><p><strong>Pick a case.</strong> Start with “Reach agreement.”</p></div>
                    <div><span className="orientation-number">02</span><p><strong>Press Next.</strong> One network event happens at a time.</p></div>
                    <div><span className="orientation-number">03</span><p><strong>Watch the logs.</strong> Three matching votes choose a value.</p></div>
                </div>
                <div className="scenario-toolbar">
                    <div className="scenario-tabs" role="tablist" aria-label="Paxos scenarios">
                        {paxosScenarios.map((item) => (
                            <button key={item.id} type="button" role="tab" aria-selected={item.id === scenario.id} className={item.id === scenario.id ? 'is-active' : ''} onClick={() => chooseScenario(item.id)}>
                                <span>{item.eyebrow}</span>{item.label}
                            </button>
                        ))}
                    </div>
                    <p>{scenario.description}</p>
                    {onTryInSandbox && guidedLink(3, scenario.id) && (
                        <button
                            type="button"
                            className="lab-try-sandbox"
                            onClick={() => onTryInSandbox(3, scenario.id)}
                        >
                            Drive this case in the Sandbox →
                        </button>
                    )}
                </div>
                <div className="debugger-grid">
                    <TraceRail frames={scenario.frames} currentIndex={currentIndex} onSelect={(index) => { setCurrentIndex(index); setPlaying(false); }} />
                    <div className="network-panel">
                        <div className="panel-label"><span>Network</span><span><i className="online-dot" /> {frame.nodes.length}-node view · quorum 3</span></div>
                        <div className="active-message-strip"><span className="active-message-dot" /><strong>{frame.event}</strong><span>{nodeLabel(frame.from)} → {frame.to?.map(nodeLabel).join(', ') || 'state only'}</span></div>
                        <PaxosNetwork frame={frame} previousFrame={previousFrame} selectedNode={selectedNode} onSelectNode={setSelectedNode} />
                        <div className="network-legend" aria-label="State legend">
                            <span><i className="legend-swatch is-empty" /> Empty</span><span><i className="legend-swatch is-accepted" /> Accepted locally</span>
                            <span><i className="legend-swatch is-chosen" /> Chosen by quorum</span><span><i className="legend-swatch is-cleared" /> Safely compacted</span>
                            <span><i className="legend-line" /> Message in flight</span>
                        </div>
                        <div className="event-readout" aria-live="polite">
                            <div className="event-type">{frame.type}</div>
                            <div><span>{frame.phase} · under the hood: {frame.event}</span><h2>{frame.summary}</h2><p>{frame.detail}</p></div>
                            <div className="quorum-readout"><span>Votes toward quorum</span><strong>{frame.quorum}</strong></div>
                        </div>
                    </div>
                    <LogInspector node={inspectedNode} />
                </div>
                <div className="debugger-controls">
                    <button type="button" onClick={() => { setCurrentIndex(0); setPlaying(false); }}>↺ <span>Reset</span></button>
                    <button type="button" aria-label="Previous event" disabled={currentIndex === 0} onClick={() => { setCurrentIndex((index) => Math.max(0, index - 1)); setPlaying(false); }}>←</button>
                    <button className="play-button" type="button" onClick={() => { if (atEnd) setCurrentIndex(0); setPlaying((value) => !value); }}>{playing ? 'Pause' : 'Play trace'} <span>{playing ? 'Ⅱ' : '▶'}</span></button>
                    <button type="button" aria-label="Next event" disabled={atEnd} onClick={() => { setCurrentIndex((index) => Math.min(scenario.frames.length - 1, index + 1)); setPlaying(false); }}>→</button>
                    <div className="step-progress"><span style={{ width: `${(currentIndex / (scenario.frames.length - 1)) * 100}%` }} /></div>
                    <span className="keyboard-hint">← → step · space play</span>
                </div>
                <StepDetails frame={frame} previousFrame={previousFrame} />
            </section>
            <section className="paxos-glossary" aria-labelledby="paxos-glossary-title">
                <div className="paxos-glossary-heading"><p className="paxos-kicker"><span /> Four words to know</p><h2 id="paxos-glossary-title">Read the trace without the paper.</h2></div>
                <dl>
                    <div><dt>Ballot</dt><dd>A versioned claim to leadership, ordered by proposal number and server address.</dd></div>
                    <div><dt>Accepted</dt><dd>One server has stored a proposed value. It can still be superseded safely.</dd></div>
                    <div><dt>Quorum</dt><dd>A majority—three of five here. Every pair of majorities overlaps.</dd></div>
                    <div><dt>Chosen</dt><dd>A quorum accepted the value. It is permanent and may execute in log order.</dd></div>
                </dl>
            </section>
            <section className="implementation-map" aria-labelledby="implementation-map-title">
                <div className="implementation-map-heading"><p className="paxos-kicker"><span /> Inside the implementation</p><h2 id="implementation-map-title">The Java fields behind the picture.</h2></div>
                <div className="implementation-map-grid">
                    <article><code>selfBallot</code><code>highestBallotPromised</code><h3>Leadership</h3><p>Orders competing leaders and rejects messages from stale ballots.</p></article>
                    <article><code>p1bResponses</code><code>commandResponses</code><h3>Quorums</h3><p>Tracks unique peer replies until the local vote plus responses crosses the majority.</p></article>
                    <article><code>Log.slots</code><code>LogSlot.status</code><h3>Replicated history</h3><p>Stores the command, ballot, result, and EMPTY / ACCEPTED / CHOSEN state for each slot.</p></article>
                    <article><code>executedCommandIndex</code><code>AMOApplication</code><h3>Ordered execution</h3><p>Executes only a contiguous chosen prefix and prevents duplicate client operations.</p></article>
                    <article><code>PingTimer</code><code>PingCheckTimer</code><h3>Stable leader</h3><p>Sends heartbeats and starts a new election after the current leader goes quiet.</p></article>
                    <article><code>AcceptCommandTimer</code><code>mergeLog</code><h3>Recovery paths</h3><p>Retries dropped Phase 2 messages and converges lagging replicas on chosen entries.</p></article>
                </div>
            </section>
            <section className="paxos-principles">
                <div className="principles-heading"><p>What the trace makes visible</p><h2>Safety is structural.<br />Liveness is conditional.</h2></div>
                <div className="principle-list">
                    <article><span>01</span><h3>Ballots order leaders</h3><p>A higher proposal number—or address tie-break—preempts every lower ballot.</p></article>
                    <article><span>02</span><h3>Quorums intersect</h3><p>Any two groups of three among five share a server, preserving knowledge of accepted values.</p></article>
                    <article><span>03</span><h3>Logs drive execution</h3><p>Commands execute only after every preceding slot is chosen, producing one linearizable history.</p></article>
                </div>
            </section>
        </>
    );
}
